<template>
  <div class="home-view">
    <!-- 移动端布局 -->
    <div class="home-view__mobile">
      <HeaderMain :searchValue.sync="searchValue" />
      <van-tabs
        v-model="activeTabName"
        color="#581c87"
        @change="onChangeTabs"
      >
        <van-tab
          v-for="{ id, title, disabled } in tabList"
          :key="id || title"
          :title="title"
          :name="title"
          :disabled="disabled"
        />
      </van-tabs>
      <template v-if="currentSubTabList && currentSubTabList.length > 0">
        <van-tabs
          v-model="activeSubTabName"
          color="#581c87"
          @change="onChangeSubTabs"
        >
          <van-tab
            v-for="{ id, title, disabled } in currentSubTabList"
            :key="id"
            :title="title"
            :name="title"
            :disabled="disabled"
          />
        </van-tabs>
      </template>

      <div
        :class="[activeSubTabName ? 'content sub-content' : 'content']"
        ref="content"
        @scroll.passive="onResourceScroll"
      >
        <div
          v-if="resourceLoading && resourceList.length === 0"
          class="state-content"
        >
          <van-loading color="#581c87">加载中...</van-loading>
        </div>
        <div
          v-else-if="resourceError && resourceList.length === 0"
          class="state-content"
        >
          <van-empty image="error" :description="resourceError" />
          <van-button size="small" color="#581c87" @click="retryResources">
            重新加载
          </van-button>
        </div>
        <template v-else>
          <ul v-if="resourceList.length > 0" class="resource-list">
            <li
              v-for="resource in resourceList"
              :key="resource.id"
              class="resource-item"
              @click="onClickMobileResourceListItem(resource)"
            >
              <van-image
                :src="resource.iconUrl || imageFaviconInternet"
                :error-icon="imageFaviconInternet"
                width="32"
                height="32"
              />
              <div class="resource-info">
                <h3 class="resource-title">{{ resource.title }}</h3>
                <p :title="resource.description" class="resource-desc">
                  {{ resource.description }}
                </p>
              </div>
            </li>
          </ul>
          <div v-else class="empty-content">
            <van-empty image="search" description="暂无数据" />
          </div>
          <div v-if="resourceList.length > 0" class="infinite-scroll-status">
            <van-loading v-if="resourceLoading" color="#fff" size="20px">
              正在加载更多...
            </van-loading>
            <button
              v-else-if="resourceLoadMoreError"
              type="button"
              class="load-error"
              @click="retryLoadMoreResources"
            >
              加载失败，点击重试
            </button>
            <span v-else-if="!hasMoreResources" class="no-more">
              已加载全部资源
            </span>
            <span v-else class="scroll-hint">继续向下滚动加载更多</span>
          </div>
        </template>
      </div>
    </div>

    <!-- PC端布局 -->
    <div class="home-view__pc">
      <aside class="aside">
        <div class="aside-header">
          <h1 class="logo">聚搜导航</h1>
          <p class="subtitle">人文社科资源导航</p>
          <van-search
            v-model="searchValue"
            :autofocus="true"
            :clearable="false"
            placeholder="请输入搜索关键词"
            class="pc-search"
          >
            <template #right-icon>
              <button
                v-if="searchValue"
                type="button"
                aria-label="清空搜索关键词"
                class="pc-search__clear"
                @mousedown.prevent
                @click.stop="clearSearch"
              >
                <van-icon name="clear" />
              </button>
            </template>
          </van-search>
        </div>
        <nav class="aside-nav">
          <div
            v-for="{ title, subTabList, open, disabled } in tabList"
            :key="title"
            class="nav-group"
          >
            <template v-if="!subTabList || subTabList.length === 0">
              <div
                :class="[
                  'nav-item',
                  { active: activeTabName === title, disabled: disabled },
                ]"
                @click="!disabled && onClickSubNavItem(title, '')"
              >
                {{ title }}
              </div>
            </template>
            <details v-else :open="open" class="nav-details">
              <summary
                :class="[
                  'nav-item',
                  'nav-item--summary',
                  { disabled: disabled },
                ]"
              >
                {{ title }}
                <van-icon name="arrow-down" class="nav-item__icon" />
              </summary>
              <div class="nav-children">
                <div
                  v-for="{
                    title: subTitle,
                    disabled: subDisabled,
                  } in subTabList"
                  :key="subTitle"
                  :class="[
                    'nav-item',
                    {
                      active:
                        activeTabName === title &&
                        activeSubTabName === subTitle,
                      disabled: subDisabled,
                    },
                  ]"
                  @click="!subDisabled && onClickSubNavItem(title, subTitle)"
                >
                  {{ subTitle }}
                </div>
              </div>
            </details>
          </div>
        </nav>
      </aside>

      <main
        class="main-content"
        ref="mainContent"
        @scroll.passive="onResourceScroll"
      >
        <div class="content-header">
          <h2>
            {{ activeTabName
            }}{{ activeSubTabName ? " - " + activeSubTabName : "" }}
          </h2>

          <p class="description">{{ categoryDescription }}</p>
        </div>
        <div
          v-if="resourceLoading && resourceList.length === 0"
          class="state-content"
        >
          <van-loading color="#581c87">加载中...</van-loading>
        </div>
        <div
          v-else-if="resourceError && resourceList.length === 0"
          class="state-content"
        >
          <van-empty image="error" :description="resourceError" />
          <van-button color="#581c87" @click="retryResources">
            重新加载
          </van-button>
        </div>
        <template v-else>
          <div v-if="resourceList.length > 0" class="resource-grid">
            <div
              v-for="resource in resourceList"
              :key="resource.id"
              class="resource-card"
              @click="onClickPcResourceListItem(resource)"
            >
              <div class="card-header">
                <van-image
                  :src="resource.iconUrl || imageFaviconInternet"
                  :error-icon="imageFaviconInternet"
                  width="48"
                  height="48"
                  class="card-icon"
                />
                <h3 class="card-title">{{ resource.title }}</h3>
              </div>
              <p :title="resource.description" class="card-description">
                {{ resource.description }}
              </p>
              <div class="card-footer">
                <span class="visit-btn">{{
                  getResourceCardButtonText(resource.type)
                }}</span>
              </div>
            </div>
          </div>
          <div v-else class="empty-content">
            <van-empty image="search" description="暂无数据" />
          </div>
          <div v-if="resourceList.length > 0" class="infinite-scroll-status">
            <van-loading v-if="resourceLoading" color="#581c87" size="20px">
              正在加载更多...
            </van-loading>
            <button
              v-else-if="resourceLoadMoreError"
              type="button"
              class="load-error"
              @click="retryLoadMoreResources"
            >
              加载失败，点击重试
            </button>
            <span v-else-if="!hasMoreResources" class="no-more">
              已加载全部资源
            </span>
            <span v-else class="scroll-hint">继续向下滚动加载更多</span>
          </div>
        </template>
      </main>
    </div>

    <van-dialog
      v-model="dialogVisitWechatOfficialAccountVisible"
      :close-on-click-overlay="true"
      :show-confirm-button="false"
      class="dialog-visit-wechat-official-account"
    >
      <VueQr
        :text="dialogVisitWechatOfficialAccountUrl"
        :size="120"
        :margin="16"
      />
      <p class="text">
        微信扫一扫可打开此内容，
        <br />
        使用完整服务
      </p>
    </van-dialog>
  </div>
</template>

<script>
import VueQr from "vue-qr";
import HeaderMain from "@/components/headers/header-main.vue";
import {
  getPublicCategories,
  getPublicResources,
} from "@/api/navigation.js";
import utils from "@/utils/index.js";
import imageFaviconInternet from "@/assets/images/favicon-internet.png";

const RESOURCE_PAGE_SIZE = 20;
const RESOURCE_SCROLL_LOAD_OFFSET = 200;
const SEARCH_DEBOUNCE_DELAY = 300;
const WECHAT_RESOURCE_TYPE = "WECHAT_OFFICIAL_ACCOUNT";

export default {
  name: "HomeView",
  components: {
    HeaderMain,
    VueQr,
  },
  data() {
    return {
      imageFaviconInternet,
      searchValue: "",
      activeTabName: "",
      tabList: [],
      resourceList: [],
      resourcePage: 1,
      resourcePageSize: RESOURCE_PAGE_SIZE,
      resourceTotalPages: 0,
      resourceLoading: false,
      resourceError: "",
      resourceLoadMoreError: false,
      resourceRequestId: 0,
      searchDebounceTimer: null,
      activeSubTabName: "",
      subTabList: [],
      dialogVisitWechatOfficialAccountVisible: false,
      dialogVisitWechatOfficialAccountUrl: "",
    };
  },
  computed: {
    categoryDescription() {
      const { activeTabName, tabList } = this;
      const category =
        tabList.find((item) => item.title === activeTabName) || {};
      return category.description || "相关学术资源";
    },
    currentSubTabList() {
      const { activeTabName, tabList } = this;
      const category = tabList.find((item) => item.title === activeTabName);
      return category?.subTabList || [];
    },
    hasMoreResources() {
      return this.resourcePage < this.resourceTotalPages;
    },
  },
  watch: {
    searchValue() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.reloadResources();
      }, SEARCH_DEBOUNCE_DELAY);
    },
  },
  methods: {
    async getTabList() {
      const categories = await getPublicCategories();
      const businessTabs = categories.map((category) => ({
        id: category.id,
        title: category.name,
        description: category.description || "",
        open: true,
        disabled: category.enabled === false,
        subTabList: category.children.map((child) => ({
          id: child.id,
          title: child.name,
          description: child.description || "",
          disabled:
            category.enabled === false || child.enabled === false,
        })),
      }));

      this.tabList = [
        {
          id: null,
          title: "全部",
          description: "",
          open: true,
          disabled: false,
          subTabList: [],
        },
        ...businessTabs,
      ];
      this.activeTabName = "全部";
      this.activeSubTabName = "";
      this.subTabList = [];
    },
    getSelectedCategoryId() {
      if (this.activeSubTabName) {
        const subCategory = this.currentSubTabList.find(
          (item) => item.title === this.activeSubTabName
        );
        return subCategory?.id;
      }

      if (this.activeTabName !== "全部") {
        return this.tabList.find(
          (item) => item.title === this.activeTabName
        )?.id;
      }

      return undefined;
    },
    async loadResources({ page, append }) {
      const requestId = ++this.resourceRequestId;
      this.resourceLoading = true;
      this.resourceError = "";
      this.resourceLoadMoreError = false;

      if (!append) {
        this.resourceList = [];
        this.resourcePage = 1;
        this.resourceTotalPages = 0;
      }

      try {
        const response = await getPublicResources({
          categoryId: this.getSelectedCategoryId(),
          keyword: this.searchValue,
          page,
          pageSize: this.resourcePageSize,
        });

        if (requestId !== this.resourceRequestId) {
          return;
        }

        this.resourceList = append
          ? [...this.resourceList, ...response.data]
          : response.data;
        this.resourcePage = response.meta.page;
        this.resourceTotalPages = response.meta.totalPages;

        if (!append) {
          this.scrollResourceContainersToTop();
        }
      } catch (error) {
        if (requestId !== this.resourceRequestId) {
          return;
        }

        console.error(error);

        if (append) {
          this.resourceLoadMoreError = true;
        } else {
          this.resourceError = "资源加载失败，请稍后重试";
        }
      } finally {
        if (requestId === this.resourceRequestId) {
          this.resourceLoading = false;
        }
      }
    },
    reloadResources() {
      return this.loadResources({ page: 1, append: false });
    },
    loadMoreResources() {
      if (
        this.resourceLoading ||
        this.resourceLoadMoreError ||
        !this.hasMoreResources
      ) {
        return;
      }

      this.loadResources({ page: this.resourcePage + 1, append: true });
    },
    retryLoadMoreResources() {
      this.resourceLoadMoreError = false;
      this.loadMoreResources();
    },
    onResourceScroll(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceToBottom <= RESOURCE_SCROLL_LOAD_OFFSET) {
        this.loadMoreResources();
      }
    },
    retryResources() {
      if (this.tabList.length === 0) {
        this.initializeData();
        return;
      }

      this.reloadResources();
    },
    scrollResourceContainersToTop() {
      this.$nextTick(() => {
        const scrollToOptions = { top: 0, behavior: "smooth" };
        this.$refs.mainContent?.scrollTo(scrollToOptions);
        this.$refs.content?.scrollTo(scrollToOptions);
      });
    },
    onChangeTabs(name) {
      this.activeTabName = name;
      const tabItem = this.tabList.find((item) => item.title === name);
      this.subTabList = Array.isArray(tabItem?.subTabList)
        ? tabItem.subTabList
        : [];
      this.activeSubTabName =
        this.subTabList.find((item) => !item.disabled)?.title || "";
      this.reloadResources();
    },
    onChangeSubTabs(name) {
      this.activeSubTabName = name;
      this.reloadResources();
    },
    onClickMobileResourceListItem(resource) {
      if (!resource.url) {
        return;
      }

      if (
        resource.type !== WECHAT_RESOURCE_TYPE ||
        utils.isWeiXin()
      ) {
        window.open(resource.url, "_blank");
        return;
      }

      this.dialogVisitWechatOfficialAccountUrl = resource.url;
      this.dialogVisitWechatOfficialAccountVisible = true;
    },
    onClickPcResourceListItem(resource) {
      if (!resource.url) {
        return;
      }

      if (resource.type !== WECHAT_RESOURCE_TYPE) {
        window.open(resource.url, "_blank");
        return;
      }

      this.dialogVisitWechatOfficialAccountUrl = resource.url;
      this.dialogVisitWechatOfficialAccountVisible = true;
    },
    getResourceCardButtonText(resourceType) {
      return resourceType === WECHAT_RESOURCE_TYPE ? "扫码关注" : "访问网站";
    },
    clearSearch() {
      this.searchValue = "";
    },
    onClickSubNavItem(tabName, subTabName) {
      this.activeTabName = tabName;
      const tabItem = this.tabList.find((item) => item.title === tabName);
      this.subTabList = Array.isArray(tabItem?.subTabList)
        ? tabItem.subTabList
        : [];
      this.activeSubTabName = subTabName;
      this.reloadResources();
    },
    async initializeData() {
      this.resourceLoading = true;
      this.resourceError = "";

      try {
        await this.getTabList();
        await this.reloadResources();
      } catch (error) {
        console.error(error);
        this.resourceList = [];
        this.resourceError = "数据加载失败，请稍后重试";
        this.resourceLoading = false;
      }
    },
  },
  created() {
    this.initializeData();
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
    this.resourceRequestId += 1;
  },
};
</script>

<style lang="scss" scoped>
.home-view {
  height: 100%;

  /* 移动端布局 - 默认显示 */
  .home-view__mobile {
    display: block;
    height: 100%;

    .content {
      padding: 16px;
      background-color: #f8f9fa;
      height: calc(100% - 54px - 44px);
      overflow-y: auto;
      background: linear-gradient(135deg, #581c87 0%, #7c3aed 100%);
      border-top: 4px solid #fff;

      &.sub-content {
        height: calc(100% - 54px - 44px - 44px);
      }

      .resource-list {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .resource-item {
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          background-color: #fff;
          border-left: 4px solid #581c87;

          .resource-info {
            flex: 1;

            .resource-title {
              margin-bottom: 4px;
              font-size: 16px;
              font-weight: 600;
              color: #333;
            }

            .resource-desc {
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              font-size: 12px;
              color: #666;
              line-height: 1.4;
            }
          }
        }
      }

      .empty-content,
      .state-content {
        padding: 16px;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        background-color: #fff;
        border-left: 4px solid #581c87;
      }

      .infinite-scroll-status {
        min-height: 48px;
        padding: 16px 0 4px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 13px;

        .load-error {
          padding: 8px 12px;
          border: 0;
          background: transparent;
          color: #fff;
          cursor: pointer;
        }

        .no-more,
        .scroll-hint {
          color: rgba(255, 255, 255, 0.8);
        }
      }
    }
  }

  /* PC端布局 */
  .home-view__pc {
    display: none;
  }

  /* PC端响应式 */
  @media screen and (min-width: 992px) {
    .home-view__mobile {
      display: none;
    }

    .home-view__pc {
      display: flex;
      height: 100%;
    }

    /* PC端侧边栏 */
    .aside {
      display: flex;
      flex-direction: column;
      width: 240px;
      height: 100%;
      background: linear-gradient(135deg, #581c87 0%, #7c3aed 100%);
      color: #fff;

      .aside-header {
        padding: 16px 20px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        .logo {
          margin-bottom: 8px;
          font-size: 32px;
          font-weight: 600;
        }

        .subtitle {
          margin-bottom: 16px;
          font-size: 14px;
          color: #fff;
        }

        .pc-search {
          padding: 0;
          background: transparent;

          ::v-deep .van-search__content {
            border-radius: 8px;
            background-color: rgba(255, 255, 255, 0.1);
          }

          ::v-deep .van-field__control {
            color: #cac9cc;
          }

          .pc-search__clear {
            padding: 4px;
            border: 0;
            background: transparent;
            color: rgba(255, 255, 255, 0.75);
            font-size: 18px;
            line-height: 1;
            cursor: pointer;
          }
        }
      }

      .aside-nav {
        padding: 24px 0;
        overflow: auto;

        .nav-group {
          margin-bottom: 8px;
        }

        .nav-item {
          padding: 12px 32px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          border-left: 3px solid transparent;

          &:hover:not(.disabled) {
            background-color: rgba(255, 255, 255, 0.1);
            border-left-color: rgba(255, 255, 255, 0.5);
          }

          &.active {
            background-color: rgba(255, 255, 255, 0.15);
            border-left-color: #fff;
          }

          &.disabled {
            cursor: not-allowed;
            opacity: 0.5;
            color: rgba(255, 255, 255, 0.5);
          }

          &--summary {
            padding-right: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            list-style: none; /* 移除默认的summary标记 */

            &::-webkit-details-marker {
              display: none; /* 移除Webkit浏览器的默认标记 */
            }

            .nav-item__icon {
              transition: transform 0.3s ease;
            }
          }
        }

        .nav-details {
          &[open] .nav-item__icon {
            transform: rotate(180deg);
          }
        }

        .nav-children {
          .nav-item {
            padding-left: 48px;
          }
        }
      }
    }

    /* PC端主内容区 */
    .main-content {
      padding: 40px;
      flex: 1;
      background-color: #f8fafc;
      overflow-y: auto;

      .content-header {
        margin-bottom: 32px;

        h2 {
          font-size: 32px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .description {
          margin: 0;
          font-size: 16px;
          color: #64748b;
        }
      }

      .resource-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 24px;
      }

      .empty-content,
      .state-content {
        min-height: 320px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }

      .infinite-scroll-status {
        min-height: 56px;
        padding: 24px 0 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;

        .load-error {
          padding: 8px 12px;
          border: 0;
          background: transparent;
          color: #dc2626;
          cursor: pointer;
        }

        .no-more,
        .scroll-hint {
          color: #64748b;
        }
      }

      .resource-card {
        padding: 24px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background: white;
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        cursor: pointer;
        border: 1px solid #e2e8f0;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          border-color: #581c87;

          .card-footer .visit-btn {
            background-color: #581c87;
            color: white;
          }
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;

          .card-icon {
            flex-shrink: 0;
          }

          .card-title {
            font-size: 20px;
            font-weight: 600;
            color: #1e293b;
            margin: 0;
          }
        }

        .card-description {
          font-size: 15px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-footer {
          display: flex;
          justify-content: flex-end;

          .visit-btn {
            font-size: 14px;
            color: #581c87;
            font-weight: 500;
            padding: 8px 16px;
            border: 1px solid #581c87;
            border-radius: 6px;
            transition: all 0.3s ease;
          }
        }
      }
    }
  }

  .dialog-visit-wechat-official-account {
    width: 280px;

    ::v-deep .van-dialog__content {
      padding: 24px 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;

      .text {
        margin: 0 24px;
        margin-top: 16px;
        font-size: 16px;
        color: #333;
        text-align: center;
      }
    }
  }
}
</style>